import React, { useState } from 'react';
import { Landmark, ArrowRightLeft, Check, Info, ChevronRight } from 'lucide-react';
import { formatCurrency, formatNumber } from '../../../../../utils/formatting';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент боковой панели с предложениями банков и сохраненными сравнениями
 */
const BankOffersSidebar = ({ 
  banks, 
  currentAmount,
  additionalAmount,
  selectedBank,
  setSelectedBank,
  formData,
  setFormData,
  savedComparisons,
  deleteSavedComparison,
  loadSavedComparison
}) => {
  const [showSavedComparisons, setShowSavedComparisons] = useState(false);
  
  // Общая сумма для рефинансирования
  const totalAmount = currentAmount + additionalAmount;
  
  // Фильтруем банки по соответствию сумме кредита
  const eligibleBanks = banks.filter(bank => 
    totalAmount >= bank.minAmount && 
    totalAmount <= bank.maxAmount
  );
  
  // Сортируем банки по минимальной ставке
  const sortedBanks = [...eligibleBanks].sort((a, b) => a.minRate - b.minRate);
  
  // Применяем ставку выбранного банка
  const applyBankRate = (bank) => {
    setSelectedBank(bank);
    
    // Устанавливаем минимальную ставку банка
    setFormData(prev => ({
      ...prev,
      newInterestRate: bank.minRate.toString()
    }));
  };
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <Landmark className="w-5 h-5 mr-2 text-indigo-500" />
          Предложения банков
        </h3>
        
        <button
          type="button"
          onClick={() => setShowSavedComparisons(prev => !prev)}
          className="text-xs text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 flex items-center transition-colors"
        >
          {showSavedComparisons ? 'Показать банки' : `Сохраненные (${savedComparisons.length})`}
          <ChevronRight className={`w-4 h-4 ml-1 transform transition-transform ${showSavedComparisons ? 'rotate-90' : ''}`} />
        </button>
      </div>

      {/* Информация о сумме кредита */}
      <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded border border-blue-100 dark:border-blue-800 text-sm text-blue-800 dark:text-blue-200">
        <div className="flex gap-2 items-center">
          <Info className="w-4 h-4 text-blue-500" />
          <div>
            <span className="font-medium">Сумма кредита: </span>
            <span>{formatCurrency(totalAmount)}</span>
          </div>
        </div>
      </div>

      {!showSavedComparisons ? (
        <div className="space-y-3">
          {sortedBanks.length > 0 ? (
            sortedBanks.map(bank => (
              <div
                key={bank.id}
                className={`p-3 rounded-lg border transition-colors cursor-pointer hover:border-indigo-300 dark:hover:border-indigo-700
                  ${selectedBank?.id === bank.id 
                    ? 'bg-indigo-50 dark:bg-indigo-900/30 border-indigo-300 dark:border-indigo-700' 
                    : 'bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700'}
                `}
                onClick={() => applyBankRate(bank)}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="font-medium text-gray-900 dark:text-white mb-1">{bank.name}</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      <div className="flex items-center gap-4 mb-1">
                        <span>От {formatNumber(bank.minRate, 1)}%</span>
                        <span className="text-xs text-gray-500 dark:text-gray-500">до {formatNumber(bank.maxRate, 1)}%</span>
                      </div>
                      <div className="text-xs">
                        <span className="text-gray-500 dark:text-gray-500">До {bank.maxTerm} лет • </span>
                        <span className="text-gray-500 dark:text-gray-500">от {formatCurrency(bank.minAmount, 0)}</span>
                      </div>
                    </div>
                  </div>
                  {selectedBank?.id === bank.id && (
                    <div className="bg-indigo-100 dark:bg-indigo-800 p-1.5 rounded-full">
                      <Check className="w-4 h-4 text-indigo-600 dark:text-indigo-300" />
                    </div>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-4 text-sm text-gray-500 dark:text-gray-400">
              {totalAmount === 0 
                ? "Введите сумму кредита, чтобы увидеть предложения банков" 
                : "Нет подходящих предложений для указанной суммы кредита"}
            </div>
          )}
          
          {sortedBanks.length > 0 && (
            <div className="mt-4 text-xs text-gray-500 dark:text-gray-400 text-center italic">
              * Ставки актуальны на апрель 2025 г. Точные условия уточняйте на сайтах банков.
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          <h4 className="font-medium text-sm text-gray-700 dark:text-gray-300 mb-2">
            Сохраненные расчеты
          </h4>
          
          {savedComparisons.length > 0 ? (
            savedComparisons.map((comparison) => (
              <div 
                key={comparison.id} 
                className="p-3 bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group">
                <div className="flex justify-between items-start mb-2">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                    {comparison.name}
                  </h4>
                  <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      type="button"
                      className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                      onClick={() => loadSavedComparison(comparison.id)}
                      title="Загрузить параметры"
                    >
                      <ArrowRightLeft className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                      onClick={() => deleteSavedComparison(comparison.id)}
                      title="Удалить"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  <div className="flex justify-between mb-1">
                    <span>Сумма:</span>
                    <span className="font-medium">
                      {formatCurrency(comparison.formData.currentLoanAmount, 0)}
                    </span>
                  </div>
                  <div className="flex justify-between mb-1">
                    <span>Новая ставка:</span>
                    <span>
                      {formatNumber(comparison.formData.newInterestRate, 2)}%
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Экономия:</span>
                    <span className={`font-semibold ${comparison.results.totalSavings > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                      {formatCurrency(comparison.results.totalSavings || 0, 0)}
                    </span>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-4 text-sm text-gray-500 dark:text-gray-400">
              У вас пока нет сохраненных расчетов
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BankOffersSidebar;